import { cn } from '@/shared/lib/utils';

/**
 * Figma 아이콘 라이브러리(icon/action · icon/sign · icon/state · icon/nav arrow …)에서 내보낸 SVG.
 * 파일 이름 규칙: <variant>-<type>-<size>.svg (Figma 변형 이름 그대로)
 * 원본은 기본색 #131927로 칠해져 있어, 그 색만 currentColor로 바꿔 그린다 → 글자 색(text-*)으로 칠한다.
 * 속이 흰 solid 아이콘(체크 원 등)은 흰 부분은 그대로 두고 바깥만 색이 바뀐다.
 */
const sources = import.meta.glob<string>('@/assets/figma/icons/*.svg', {
  query: '?raw',
  import: 'default',
  eager: true,
});

const svgByName: Record<string, string> = Object.fromEntries(
  Object.entries(sources).map(([path, svg]) => [
    path.replace(/^.*\/(.+)\.svg$/, '$1'),
    svg.replace(/#131927/g, 'currentColor'),
  ]),
);

export type IconName =
  | 'cancel-line-14'
  | 'cancel-line-16'
  | 'cancel-line-24'
  | 'check-circle-line-24'
  | 'check-circle-solid-16'
  | 'check-circle-solid-24'
  | 'check-line-16'
  | 'check-line-24'
  | 'circle-line-24'
  | 'copy-line-24'
  | 'delete-circle-solid-24'
  | 'download-line-24'
  | 'help-circle-solid-14'
  | 'info-circle-line-24'
  | 'info-circle-solid-24'
  | 'logout-line-16'
  | 'minus-line-16'
  | 'nav-arrow-down-line-16'
  | 'nav-arrow-down-line-24'
  | 'nav-arrow-left-line-16'
  | 'nav-arrow-right-line-14'
  | 'nav-arrow-right-line-16'
  | 'nav-arrow-up-line-16'
  | 'page-left-solid-16'
  | 'page-right-solid-16'
  | 'page-solid-24'
  | 'pharmacy-cross-solid-24'
  | 'plus-line-16'
  | 'search-line-24'
  | 'user-solid-24'
  | 'warning-circle-line-24'
  | 'warning-circle-solid-14'
  | 'warning-circle-solid-24';

export interface IconProps {
  name: IconName;
  className?: string;
  /** 스크린 리더에 읽힐 이름. 없으면 장식으로 보고 숨긴다 */
  label?: string;
}

/** 크기는 SVG의 width · height(파일 이름 끝 숫자)를 그대로 쓴다. 색은 className의 text-* */
export const Icon = ({ name, className, label }: IconProps) => (
  <span
    role={label ? 'img' : undefined}
    aria-label={label}
    aria-hidden={label ? undefined : true}
    className={cn('inline-flex shrink-0 [&>svg]:block', className)}
    // 저장소 안의 고정 SVG만 넣는다 (사용자 입력 아님)
    dangerouslySetInnerHTML={{ __html: svgByName[name] ?? '' }}
  />
);
